import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  X, MessageSquare, ClipboardList, FileSearch, IdCard, Mic, Send, Volume2,
  CheckCircle2, ArrowUpRight, Clock, TrendingUp, ShieldCheck, FileText, Sparkles,
  Phone, PhoneOff, Loader2,
} from "lucide-react";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { StatusBadge } from "@/components/StatusBadge";
import { VoiceWaveform } from "@/components/VoiceWaveform";
import { useStore } from "@/lib/store";
import { useAuth } from "@/lib/auth";
import { AUTHORITY, EXCEPTIONS, SUGGESTED_QUESTIONS } from "@/data/seed";
import { employeeReply } from "@/lib/convo";
import { API } from "@/lib/api";
import { VoiceCall } from "@/components/VoiceCall";
import { Portal } from "@/components/Portal";
import { toast } from "sonner";

function Stat({ icon: Icon, label, value, color = "#00E5FF" }) {
  return (
    <div className="rounded-xl border border-white/8 bg-[#030A16]/40 p-3" data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <Icon size={15} style={{ color }} />
      <div className="mt-1.5 font-display text-xl font-semibold text-white">{value}</div>
      <div className="text-[11px] text-slate-400">{label}</div>
    </div>
  );
}

function TalkView({ employee, onStartCall, userName = "Lewis" }) {
  const [messages, setMessages] = useState([{ from: "ai", text: employee.greeting }]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const scroller = useRef(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const suggestions = SUGGESTED_QUESTIONS[employee.id] || ["What are you working on?", "What needs my approval?"];
  const context = EXCEPTIONS.filter((e) => e.owner === employee.id).slice(0, 3);

  useEffect(() => { scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" }); }, [messages, thinking]);

  // Real LLM reply (falls back to the local responder if the API is unavailable).
  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q) return;
    const hist = [...messagesRef.current, { from: "user", text: q }];
    setMessages(hist);
    setInput("");
    setThinking(true);
    try {
      const res = await fetch(`${API}/employee/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ employee, message: q, history: hist, user_name: userName }),
      });
      if (!res.ok) throw new Error("reply");
      const data = await res.json();
      setMessages((m) => [...m, { from: "ai", text: data.reply }]);
    } catch (_) {
      setMessages((m) => [...m, { from: "ai", text: employeeReply(employee, q) }]);
    } finally {
      setThinking(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* header */}
      <div className="flex items-center gap-3 border-b border-white/8 px-5 py-4">
        <EmployeeAvatar employee={employee} size={52} showStatus={false} />
        <div className="min-w-0 flex-1">
          <div className="font-display font-semibold text-white">{employee.name}</div>
          <div className="text-xs text-slate-400" data-testid="talk-status">{thinking ? "Thinking…" : "Type, or tap Voice to talk out loud"}</div>
        </div>
        <button onClick={onStartCall} className="flex shrink-0 items-center gap-1.5 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-3.5 py-2 text-xs font-semibold text-[#030A16] transition-transform hover:-translate-y-0.5" data-testid="talk-voice-btn">
          <Phone size={14} /> Voice
        </button>
      </div>

      {/* conversation */}
      <div ref={scroller} className="flex-1 space-y-3 overflow-y-auto px-5 py-4" data-testid="talk-conversation">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                m.from === "user"
                  ? "bg-gradient-to-r from-[#00E5FF] to-[#39C0FF] text-[#030A16]"
                  : "border border-white/10 bg-[#0A1326] text-slate-100"
              }`}
            >
              {m.text}
            </div>
          </div>
        ))}
        {thinking && (
          <div className="flex justify-start"><div className="rounded-2xl border border-white/10 bg-[#0A1326] px-3 py-2 text-xs text-slate-400">thinking…</div></div>
        )}
      </div>

      {/* context panel */}
      {context.length > 0 && (
        <div className="border-t border-white/8 px-5 py-3">
          <div className="mb-2 flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wide text-slate-500"><FileText size={12} /> Referenced records</div>
          <div className="flex gap-2 overflow-x-auto">
            {context.map((c) => (
              <div key={c.id} className="min-w-[150px] rounded-lg border border-white/10 bg-[#030A16]/50 p-2.5" data-testid={`context-${c.id}`}>
                <div className="font-mono text-xs text-[#00E5FF]">{c.id}</div>
                <div className="mt-0.5 truncate text-xs text-slate-300">{c.site}</div>
                <div className="text-[11px] text-slate-500">{c.issue}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* suggestions */}
      <div className="flex flex-wrap gap-2 px-5 pb-2 pt-3">
        {suggestions.map((s, i) => (
          <button key={s} onClick={() => send(s)} className="rounded-full border border-white/10 px-3 py-1 text-xs text-slate-300 transition-colors hover:border-[#00E5FF]/40 hover:text-white" data-testid={`suggested-question-${i}`}>
            {s}
          </button>
        ))}
      </div>

      {/* input */}
      <div className="flex items-center gap-2 border-t border-white/8 p-4">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={`Message ${employee.name}…`}
          className="h-10 flex-1 rounded-full border border-white/10 bg-[#030A16]/60 px-4 text-sm text-white placeholder:text-slate-500 focus:border-[#00E5FF]/50"
          data-testid="talk-input"
        />
        <button onClick={() => send()} className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] text-[#030A16]" aria-label="Send" data-testid="talk-send-btn">
          <Send size={17} />
        </button>
      </div>
    </div>
  );
}

function AssignTask({ employee, onDone }) {
  const [task, setTask] = useState("");
  const { assignTask, addActivity } = useStore();
  const submit = () => {
    if (!task.trim()) return;
    assignTask(employee.id, task.trim());
    addActivity({ employee: employee.id, task: `New assignment: ${task.trim()}`, source: "Owner", action: "Task assigned by Lewis", consulted: [], approval: "N/A", result: "Queued", icon: "ClipboardList" });
    toast.success(`Task assigned to ${employee.name}`, { description: task.trim() });
    setTask("");
    onDone();
  };
  return (
    <div className="rounded-xl border border-white/10 bg-[#030A16]/50 p-4" data-testid="assign-task-form">
      <div className="mb-2 text-sm font-medium text-white">Assign a task to {employee.name}</div>
      <textarea value={task} onChange={(e) => setTask(e.target.value)} rows={3} placeholder="Describe what you'd like done…" className="w-full rounded-lg border border-white/10 bg-[#030A16] p-3 text-sm text-white placeholder:text-slate-500 focus:border-[#00E5FF]/50" data-testid="assign-task-input" />
      <div className="mt-2 flex justify-end gap-2">
        <button onClick={onDone} className="rounded-lg px-3 py-1.5 text-sm text-slate-400 hover:text-white">Cancel</button>
        <button onClick={submit} className="rounded-lg bg-[#00E5FF] px-4 py-1.5 text-sm font-semibold text-[#030A16]" data-testid="assign-task-submit">Assign</button>
      </div>
    </div>
  );
}

export function EmployeePanel({ employeeId, onClose, initialMode = "office" }) {
  const { state } = useStore();
  const { user } = useAuth();
  const userName = user?.call_name || "Lewis";
  const navigate = useNavigate();
  const employee = state.employees.find((e) => e.id === employeeId);
  const [mode, setMode] = useState(initialMode);
  const [assigning, setAssigning] = useState(false);
  const [call, setCall] = useState(false);
  useEffect(() => { setMode(initialMode); }, [initialMode, employeeId]);
  if (!employee) return null;

  const timeline = state.activity.filter((a) => a.employee === employee.id).slice(0, 6);
  const auth = AUTHORITY[employee.authority];

  return (
    <Portal>
    <>
    {call && <VoiceCall employee={employee} onClose={() => setCall(false)} onSwitchToText={() => setMode("talk")} />}
    <AnimatePresence>
      <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
        <motion.div className="absolute inset-0 bg-black/60 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
        <motion.aside
          className="glass-strong absolute inset-y-0 right-0 flex w-full max-w-md flex-col border-l border-white/10"
          initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
          transition={{ type: "spring", stiffness: 260, damping: 30 }}
          data-testid="employee-panel"
        >
          <div className="flex items-center justify-between border-b border-white/8 px-5 py-3">
            <div className="flex rounded-lg border border-white/10 p-0.5">
              <button onClick={() => setMode("office")} className={`rounded-md px-3 py-1 text-xs ${mode === "office" ? "bg-[#00E5FF] text-[#030A16]" : "text-slate-300"}`} data-testid="panel-tab-office">Office</button>
              <button onClick={() => setMode("talk")} className={`rounded-md px-3 py-1 text-xs ${mode === "talk" ? "bg-[#00E5FF] text-[#030A16]" : "text-slate-300"}`} data-testid="panel-tab-talk">Message</button>
            </div>
            <button onClick={onClose} aria-label="Close panel" data-testid="panel-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
          </div>

          {mode === "talk" ? (
            <TalkView employee={employee} onStartCall={() => setCall(true)} userName={userName} />
          ) : (
            <>
            <div className="flex-1 overflow-y-auto px-5 py-5">
              <div className="flex items-center gap-4">
                <EmployeeAvatar employee={employee} size={80} />
                <div>
                  <div className="font-display text-2xl font-semibold text-white">{employee.name}</div>
                  <div className="text-sm text-slate-300">{employee.title}</div>
                  <div className="mt-2"><StatusBadge status={employee.status} /></div>
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-white/8 bg-[#030A16]/40 p-3">
                <div className="text-[11px] font-mono uppercase tracking-wide text-slate-500">Current assignment</div>
                <div className="mt-1 text-sm text-slate-200">{employee.assignment}</div>
              </div>

              <div className="mt-3 flex items-start gap-2 rounded-xl border border-[#00E5FF]/20 bg-[#00E5FF]/5 p-3">
                <Sparkles size={15} className="mt-0.5 shrink-0 text-[#00E5FF]" />
                <p className="text-sm italic text-slate-200">"{employee.greeting}"</p>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2.5">
                <Stat icon={CheckCircle2} label="Tasks today" value={employee.tasksToday} />
                <Stat icon={TrendingUp} label="Tasks month" value={employee.tasksMonth} color="#8A2BE2" />
                <Stat icon={ClipboardList} label="Open" value={employee.openAssignments} color="#FFBF00" />
                <Stat icon={ArrowUpRight} label="Escalated" value={employee.escalated} color="#FF0055" />
                <Stat icon={Clock} label="Hours saved" value={employee.hoursSaved} />
                <Stat icon={ShieldCheck} label="Authority" value={auth.label} color="#22D3EE" />
              </div>

              <div className="mt-5">
                <div className="mb-2 text-[11px] font-mono uppercase tracking-wide text-slate-500">Activity timeline</div>
                <div className="space-y-2">
                  {timeline.length === 0 && <div className="text-sm text-slate-500">No recent activity.</div>}
                  {timeline.map((a) => (
                    <div key={a.id} className="flex gap-3 rounded-lg border border-white/8 bg-[#030A16]/40 p-2.5">
                      <span className="font-mono text-xs text-[#00E5FF]">{a.time}</span>
                      <div className="min-w-0">
                        <div className="truncate text-sm text-slate-200">{a.task}</div>
                        <div className="truncate text-[11px] text-slate-500">{a.result}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-5">
                <div className="mb-2 text-[11px] font-mono uppercase tracking-wide text-slate-500">Permissions</div>
                <div className="rounded-lg border border-white/8 bg-[#030A16]/40 p-3 text-sm text-slate-300">
                  <div className="text-white">{auth.label}</div>
                  <div className="text-xs text-slate-400">{auth.desc}</div>
                </div>
              </div>

              {assigning && (
                <div className="mt-5"><AssignTask employee={employee} onDone={() => setAssigning(false)} /></div>
              )}
            </div>

            {!assigning && (
              <div className="shrink-0 border-t border-white/8 bg-[#0A1326]/70 p-4 backdrop-blur-md">
                <div className="grid grid-cols-2 gap-2.5">
                  <button onClick={() => setCall(true)} className="col-span-2 flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-4 py-3 text-sm font-semibold text-[#030A16] transition-transform hover:-translate-y-0.5" data-testid="btn-talk-to-employee">
                    <Phone size={17} /> Talk to {employee.name} by Voice
                  </button>
                  <button onClick={() => setMode("talk")} className="col-span-2 flex items-center justify-center gap-2 rounded-lg border border-white/10 px-3 py-2.5 text-sm text-slate-200 hover:border-[#00E5FF]/40 hover:text-white" data-testid="btn-message-employee">
                    <MessageSquare size={15} /> Message instead
                  </button>
                  <button onClick={() => setAssigning(true)} className="flex items-center justify-center gap-2 rounded-lg border border-white/10 px-3 py-2.5 text-sm text-slate-200 hover:border-[#00E5FF]/40 hover:text-white" data-testid="btn-assign-task">
                    <ClipboardList size={15} /> Assign Task
                  </button>
                  <button onClick={() => toast.info(`Reviewing ${employee.name}'s work`, { description: "Latest outputs shown in the activity timeline." })} className="flex items-center justify-center gap-2 rounded-lg border border-white/10 px-3 py-2.5 text-sm text-slate-200 hover:border-[#00E5FF]/40 hover:text-white" data-testid="btn-review-work">
                    <FileSearch size={15} /> Review Work
                  </button>
                  <button onClick={() => { onClose(); navigate(`/employees/${employee.id}`); }} className="col-span-2 flex items-center justify-center gap-2 rounded-lg border border-white/10 px-3 py-2.5 text-sm text-slate-200 hover:border-[#00E5FF]/40 hover:text-white" data-testid="btn-open-profile">
                    <IdCard size={15} /> Open Associate Profile
                  </button>
                </div>
              </div>
            )}
            </>
          )}
        </motion.aside>
      </div>
    </AnimatePresence>
    </>
    </Portal>
  );
}
